import React, {useEffect, useRef, useState} from "react";
import {Input} from "reactstrap";
import {observer} from "mobx-react-lite";
import {TextObservable} from "../../../stores/observables/textObservable";
import "./input.sass";
import classNames from "classnames";
import {useTranslation} from "react-i18next";

export type TextBoxType = "text" | "textarea";

export interface ITextBoxProps {
  /**
   * The type of input control
   */
  type: TextBoxType;
  /**
   * The value of the input
   */
  value: TextObservable;
  /**
   * The validation error to show
   */
  errorText?: string | null;
  /**
   * The placeholder text
   */
  placeholder?: string;
  /**
   * A flag indicating if the control is disabled or not
   */
  disabled?: boolean;
  /**
   * A flag indicating if the input should display the number of characters typed
   */
  showTextCount?: boolean;
  /**
   * Controls the number of rows to display for multiline control
   */
  rows?: number;
  /**
   * A flag indicating if the control is readonly or not
   */
  readOnly?: boolean;
  /**
   * ID of the input
   */
  id?: string;
}

const defaultPaddingOffset = 18; // 18px from right
const inputPaddingOffset = 7; // 7px from right when no error and has text counter
const inputErrorPaddingOffset = 25; // 25px added when has error and has text counter

const TextBox: React.FC<ITextBoxProps> = ({
  type,
  value,
  errorText = null,
  placeholder,
  disabled,
  showTextCount = true,
  rows = 3,
  readOnly = false,
  id = ""
}) => {
  const [finalValidationMessage, setFinalValidationMessage] = useState("");
  const {t} = useTranslation(["translation"]);

  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    value.setText(event.target.value);
  }

  useEffect(() => {
    let messages: string = "";
    if (!value.isLengthValid()) {
      messages = t("translation:controls.characterLimitExceeded");
    }
    if (!value.validate?.(value.text)) {
      messages = errorText || "";
    }
    setFinalValidationMessage(messages);
  }, [errorText, value, value.text, t]);

  useEffect(() => {
    let inputWidth: number = defaultPaddingOffset;
    if (textCounterRef.current) {
      if (showTextCount) {
        inputWidth += inputPaddingOffset + textCounterRef.current.offsetWidth;
        if (!value.isValid) {
          inputWidth += inputErrorPaddingOffset;
        }
      }
    }
    if (inputRef && inputRef.current) {
      inputRef.current!.style.setProperty(
        "padding-right",
        `${inputWidth}px`,
        "important"
      );
    }
  }, [showTextCount, value.text, value.isValid]);

  const textCounterRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className={classNames("input-box", {
        "has-text-counter": showTextCount && value.maxLength,
        "is-invalid": !value.isValid
      })}
    >
      <Input
        type={type}
        inputMode="text"
        value={value.text ?? ""}
        onChange={handleTextChange}
        placeholder={placeholder}
        invalid={!value.isValid}
        disabled={disabled}
        rows={rows}
        innerRef={inputRef}
        readOnly={readOnly}
        id={id}
      />
      {showTextCount && value.maxLength && (
        <div
          className="text-counter"
          data-testid="textBoxCounter"
          ref={textCounterRef}
        >
          {`${value.text.length}/${value.maxLength}`}
        </div>
      )}
      {finalValidationMessage && <label>{finalValidationMessage}</label>}
    </div>
  );
};

export default observer(TextBox);
